import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../config/api';
import { 
  FiBell, 
  FiCheckCircle, 
  FiAlertCircle, 
  FiClock,
  FiTrash2,
  FiFilter,
  FiInbox,
  FiRefreshCw,
  FiZap,
  FiSun,
  FiCloudOff,
  FiAlertTriangle
} from 'react-icons/fi';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    unread: 0
  });

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    if (!refreshing) setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(`${API_URL}/api/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('📨 Respuesta del backend:', response.data); // Para debug

      let notificationsData = [];
      
      // 👇 ESTRUCTURA CORREGIDA - El backend envía { success, notifications }
      if (response.data?.success && response.data?.notifications) {
        notificationsData = response.data.notifications;
      } 
      // 👇 Estructura anterior (por compatibilidad)
      else if (response.data?.data) {
        notificationsData = response.data.data;
      } 
      // 👇 Si es array directo
      else if (Array.isArray(response.data)) {
        notificationsData = response.data;
      }
      // 👇 Si está en response.data.data (como esperaba antes)
      else if (response.data?.data && Array.isArray(response.data.data)) {
        notificationsData = response.data.data;
      }

      console.log('📋 Notificaciones cargadas:', notificationsData.length);
      setNotifications(notificationsData);
      calculateStats(notificationsData);
    } catch (error) {
      console.error('❌ Error fetching notifications:', error);
      setError('No se pudieron cargar las notificaciones');
      
      // Datos de ejemplo offline
      if (!navigator.onLine) {
        const offlineData = [
          {
            _id: '1',
            type: 'urgente',
            title: '⚠️ Cliente URGENTE',
            message: 'Juan Pérez marcado como urgente - Revisar inmediatamente',
            isRead: false,
            createdAt: new Date().toISOString(),
            client: { name: 'Juan Pérez', company: 'Empresa A' }
          },
          {
            _id: '2',
            type: 'sin_luz',
            title: '💡 Cliente sin luz',
            message: 'María García reporta sin servicio de luz',
            isRead: false,
            createdAt: new Date(Date.now() - 1800000).toISOString(),
            client: { name: 'María García', company: 'Empresa B' }
          },
          {
            _id: '3',
            type: 'vencimiento',
            title: '📅 Próximo vencimiento',
            message: 'Carlos López tiene vencimiento en 2 días',
            isRead: true,
            createdAt: new Date(Date.now() - 3600000).toISOString(),
            client: { name: 'Carlos López', company: 'Empresa C' }
          },
          {
            _id: '4',
            type: 'desconectado',
            title: '🔌 Cliente desconectado',
            message: 'Ana Martínez ha sido desconectada del servicio',
            isRead: true,
            createdAt: new Date(Date.now() - 86400000).toISOString(),
            client: { name: 'Ana Martínez', company: 'Empresa D' }
          }
        ];
        setNotifications(offlineData);
        calculateStats(offlineData);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const calculateStats = (data) => {
    setStats({
      total: data.length,
      unread: data.filter(n => !n.isRead).length
    });
  };

  const markAsRead = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/notifications/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const updated = notifications.map(n => 
        n._id === id ? { ...n, isRead: true } : n
      );
      setNotifications(updated);
      calculateStats(updated);
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const updated = notifications.map(n => ({ ...n, isRead: true }));
      setNotifications(updated);
      calculateStats(updated);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const deleteNotification = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/notifications/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const filtered = notifications.filter(n => n._id !== id);
      setNotifications(filtered);
      calculateStats(filtered);
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchNotifications();
  };

  const getNotificationIcon = (type) => {
    switch(type) {
      case 'urgente': return <FiAlertTriangle className="text-red-500" size={20} />;
      case 'sin_luz': return <FiCloudOff className="text-yellow-500" size={20} />;
      case 'desconectado': return <FiZap className="text-gray-500" size={20} />;
      case 'vencimiento': return <FiClock className="text-orange-500" size={20} />;
      case 'pago_recibido': return <FiCheckCircle className="text-green-500" size={20} />;
      case 'recordatorio': return <FiBell className="text-blue-500" size={20} />;
      default: return <FiBell className="text-blue-500" size={20} />;
    }
  };

  const getNotificationColor = (type) => {
    switch(type) {
      case 'urgente': return 'bg-red-50 border-red-200';
      case 'sin_luz': return 'bg-yellow-50 border-yellow-200';
      case 'desconectado': return 'bg-gray-50 border-gray-200';
      case 'vencimiento': return 'bg-orange-50 border-orange-200';
      case 'pago_recibido': return 'bg-green-50 border-green-200';
      case 'recordatorio': return 'bg-blue-50 border-blue-200';
      default: return 'bg-blue-50 border-blue-200';
    }
  };

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !n.isRead;
    return n.type === filter;
  });

  if (loading && !refreshing) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Notificaciones</h1>
          <p className="text-sm md:text-base text-gray-600 mt-1">
            Centro de notificaciones y alertas
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <FiRefreshCw className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} size={16} />
            <span className="hidden sm:inline">Actualizar</span>
          </button>
          {stats.unread > 0 && (
            <button
              onClick={markAllAsRead}
              className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              <FiCheckCircle className="mr-2" size={16} />
              <span className="hidden sm:inline">Leer todo</span>
              <span className="sm:hidden">Leer</span>
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-3 md:gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <FiBell className="text-blue-600 text-xl" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">No leídas</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.unread}</p>
            </div>
            <div className="bg-yellow-100 p-3 rounded-full">
              <FiClock className="text-yellow-600 text-xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap items-center gap-2">
          <FiFilter className="text-gray-400" size={18} />
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1 rounded-lg transition-colors text-sm ${
              filter === 'all' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Todas
          </button>
          <button
            onClick={() => setFilter('unread')}
            className={`px-3 py-1 rounded-lg transition-colors text-sm ${
              filter === 'unread' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            No leídas
          </button>
          <button
            onClick={() => setFilter('urgente')}
            className={`px-3 py-1 rounded-lg transition-colors text-sm ${
              filter === 'urgente' 
                ? 'bg-red-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Urgentes
          </button>
          <button
            onClick={() => setFilter('sin_luz')}
            className={`px-3 py-1 rounded-lg transition-colors text-sm ${
              filter === 'sin_luz' 
                ? 'bg-yellow-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Sin Luz
          </button>
          <button
            onClick={() => setFilter('vencimiento')}
            className={`px-3 py-1 rounded-lg transition-colors text-sm ${
              filter === 'vencimiento' 
                ? 'bg-orange-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Vencimientos
          </button>
        </div>
      </div>

      {/* Notifications List */}
      <div className="bg-white rounded-lg shadow divide-y">
        {filteredNotifications.length === 0 ? (
          <div className="p-8 text-center">
            <FiInbox className="mx-auto text-4xl text-gray-300 mb-4" />
            <p className="text-gray-500">No hay notificaciones</p>
            <p className="text-sm text-gray-400 mt-2">
              {filter !== 'all' ? 'Prueba con otro filtro' : 'Las notificaciones aparecerán aquí cuando haya actividad'}
            </p>
          </div>
        ) : (
          filteredNotifications.map((notification) => (
            <div
              key={notification._id}
              className={`p-4 hover:bg-gray-50 transition-colors ${
                !notification.isRead ? 'bg-blue-50' : ''
              } ${getNotificationColor(notification.type)}`}
            >
              <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                <div className="flex-shrink-0 hidden sm:block">
                  {getNotificationIcon(notification.type)}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div className="sm:hidden">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <p className={`text-sm font-medium ${
                        !notification.isRead ? 'text-gray-900' : 'text-gray-600'
                      }`}>
                        {notification.title || 'Notificación'}
                      </p>
                      {!notification.isRead && (
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full sm:hidden">
                          Nueva
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2 ml-7 sm:ml-0">
                      <span className="text-xs text-gray-500">
                        {notification.createdAt && formatDistanceToNow(new Date(notification.createdAt), {
                          addSuffix: true,
                          locale: es
                        })}
                      </span>
                      {!notification.isRead && (
                        <button
                          onClick={() => markAsRead(notification._id)}
                          className="text-blue-600 hover:text-blue-800 p-1"
                          title="Marcar como leída"
                        >
                          <FiCheckCircle size={16} />
                        </button>
                      )}
                      <button
                        onClick={() => deleteNotification(notification._id)}
                        className="text-red-600 hover:text-red-800 p-1"
                        title="Eliminar"
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-600 mt-2 ml-7 sm:ml-0">
                    {notification.message}
                  </p>
                  
                  {notification.clientId && (
                    <div className="mt-2 text-xs text-gray-500 ml-7 sm:ml-0">
                      Cliente: {notification.clientId?.fullName || notification.clientId?.name || 'N/A'} 
                      {notification.clientId?.company && ` · ${notification.clientId.company}`}
                      {notification.metadata?.clientNumber && ` · #${notification.metadata.clientNumber}`}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {error && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <p className="text-sm text-yellow-700">{error}</p>
        </div>
      )}
    </div>
  );
};

export default Notifications;